import React, { useState, useEffect } from 'react';
import { Card, Col, Container, Row, Form, Offcanvas, Button } from 'react-bootstrap';
import BreadCrumb from 'Common/BreadCrumb';
import Chart from 'react-apexcharts';
import Swal from 'sweetalert2'; // Import SweetAlert2
import axios from 'axios'; // Import Axios

const Logs = () => {
  document.title = 'Logs | CITY SERVICE';

  const [filter, setFilter] = useState('Weekly');
  const [showOffcanvas, setShowOffcanvas] = useState(false); // State for offcanvas visibility
  interface Log {
    id: number;
    timestamp: string;
    level: string;
    message: string;
  }

  const [logs, setLogs] = useState<Log[]>([]); // State for logs fetched from API
  const [selectedLog, setSelectedLog] = useState<Log | null>(null); // State for selected log details

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
        if (!token) {
          throw new Error('Authentication token is missing. Please log in again.');
        }

        Swal.fire({
          title: 'Loading...',
          text: 'Fetching logs data',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const response = await axios.get(`/logs?filter=${filter}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (Array.isArray(response.data)) {
          setLogs(response.data); // Update the logs state with fetched data
        } else {
          console.error('Unexpected response format:', response.data);
          setLogs([]);
        }
      } catch (error) {
        console.error('Error fetching logs:', error);
        setLogs([]);
      } finally {
        Swal.close(); // Close SweetAlert2 loading
      }
    };

    fetchLogs(); // Call the fetchLogs function when the component mounts or filter changes
  }, [filter]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
  };

  const handleViewLog = (log: any) => {
    setSelectedLog(log); // Set the selected log details
    setShowOffcanvas(true); // Show the offcanvas
  };

  const handleCloseOffcanvas = () => {
    setShowOffcanvas(false); // Hide the offcanvas
    setSelectedLog(null); // Clear the selected log details
  };

  const chartOptions = {
    chart: {
      type: 'line',
      height: 350,
      toolbar: {
        show: false,
      },
    },
    stroke: {
      curve: 'smooth', 
      width: 3,
    },
    markers: {
      size: 5,
      hover: {
        size: 7,
      },
    },
    colors: ['#FF6961'], // Set line color to red pastel
    xaxis: {
      categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'],
      labels: {
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      title: {
        text: 'Values',
        style: {
          fontSize: '14px',
        },
      },
      labels: {
        style: {
          fontSize: '12px',
        },
      },
    },
    title: {
      text: 'Logs Analysis',
      align: 'left',
      style: {
        fontSize: '16px',
        fontWeight: 'bold',
      },
    },
    tooltip: {
      theme: 'dark',
    },
    grid: {
      borderColor: '#e7e7e7',
      strokeDashArray: 5,
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      markers: {
        radius: 12,
      },
    },
  };

  const chartSeries = [
    {
      name: 'Series 1',
      data: [30, 40, 35, 50, 49, 60, 70, 91, 125],
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="Logs" title="Logs Recruitments" />

          <Row className="mb-3">
            <Col>
              <Form.Group controlId="filterRadio">
                <Form.Label style={{ color: '#000000' }}>Filter Logs</Form.Label>
                <div>
                  <Form.Check
                    type="radio"
                    id="filterWeekly"
                    label="Weekly"
                    name="filter"
                    value="Weekly"
                    checked={filter === 'Weekly'}
                    onChange={handleFilterChange}
                    inline
                  />
                  <Form.Check
                    type="radio"
                    id="filterMonthly"
                    label="Monthly"
                    name="filter"
                    value="Monthly"
                    checked={filter === 'Monthly'}
                    onChange={handleFilterChange}
                    inline
                  />
                  <Form.Check
                    type="radio"
                    id="filterYearly"
                    label="Yearly"
                    name="filter"
                    value="Yearly"
                    checked={filter === 'Yearly'}
                    onChange={handleFilterChange}
                    inline
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col lg={8}>
              <Card>
                <Card.Header className="p-3" style={{ backgroundColor: '#181a21', color: '#fff' }}>
                  <h5 className="card-title mb-0">Logs</h5>
                </Card.Header>
                <Card.Body>
                  <Chart options={chartOptions} series={chartSeries} type="line" height={350} />
                </Card.Body>
              </Card>
              <Card className="mt-3">
                <Card.Header className="p-3" style={{ backgroundColor: '#181a21', color: '#fff' }}>
                  <h5 className="card-title mb-0">Log Details</h5>
                </Card.Header>
                <Card.Body style={{ height: '600px', overflowY: 'auto' }}>
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Timestamp</th>
                        <th>Level</th>
                        <th>Message</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id}>
                          <td>{log.id}</td>
                          <td>{log.timestamp}</td>
                          <td>{log.level}</td>
                          <td>{log.message}</td>
                          <td>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleViewLog(log)}
                            >
                              View Log
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card>
                <Card.Header className="p-3" style={{ backgroundColor: '#181a21', color: '#fff' }}>
                  <h5 className="card-title mb-0">Rasio Error</h5>
                </Card.Header>
                <Card.Body style={{ height: '400px' }}>
                  <Chart
                    options={{
                      labels: ['Error', 'Success'], // Updated legend labels
                      colors: ['#FF6961', '#87CEEB'], // Updated Success color to sky blue
                      legend: {
                        position: 'bottom',
                      },
                      title: {
                        text: 'Error vs Success Distribution',
                        align: 'center',
                        style: {
                          fontSize: '14px',
                          fontWeight: 'bold',
                        },
                      },
                      dataLabels: {
                        formatter: (val: number) => `${val.toFixed(2)}%`, // Display percentages
                      },
                      tooltip: {
                        y: {
                          formatter: (val: number) => `${val}%`, // Tooltip shows percentage
                        },
                      },
                    }}
                    series={[30, 70]} // Data for Error (30%) and Success (70%)
                    type="pie"
                    height={350}
                  />
                </Card.Body>
              </Card>
              <Card className="mt-3">
                <Card.Header className="p-3" style={{ backgroundColor: '#181a21', color: '#fff' }}>
                  <h5 className="card-title mb-0">Top 10 Most Frequent Errors</h5>
                </Card.Header>
                <Card.Body style={{ height: '600px', overflowY: 'auto' }}>
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>End Point</th>
                        <th>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>1</td>
                        <td>/api/v1/users</td>
                        <td>120</td>
                      </tr>
                      <tr>
                        <td>2</td>
                        <td>/api/v1/orders</td>
                        <td>95</td>
                      </tr>
                      <tr>
                        <td>3</td>
                        <td>/api/v1/products</td>
                        <td>80</td>
                      </tr>
                      <tr>
                        <td>4</td>
                        <td>/api/v1/auth</td>
                        <td>75</td>
                      </tr>
                      <tr>
                        <td>5</td>
                        <td>/api/v1/payments</td>
                        <td>60</td>
                      </tr>
                      <tr>
                        <td>6</td>
                        <td>/api/v1/reports</td>
                        <td>50</td>
                      </tr>
                      <tr>
                        <td>7</td>
                        <td>/api/v1/settings</td>
                        <td>45</td>
                      </tr>
                      <tr>
                        <td>8</td>
                        <td>/api/v1/notifications</td>
                        <td>40</td>
                      </tr>
                      <tr>
                        <td>9</td>
                        <td>/api/v1/logs</td>
                        <td>35</td>
                      </tr>
                      <tr>
                        <td>10</td>
                        <td>/api/v1/dashboard</td>
                        <td>30</td>
                      </tr>
                    </tbody>
                  </table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Offcanvas for viewing log details */}
          <Offcanvas show={showOffcanvas} onHide={handleCloseOffcanvas} placement="end" style={{ width: '80%' }} className="offcanvas-70">
            <Offcanvas.Header closeButton>
              <Offcanvas.Title>Log Details</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              {selectedLog ? (
                <div>
                  <p><strong>ID:</strong> {selectedLog.id}</p>
                  <p><strong>Timestamp:</strong> {selectedLog.timestamp}</p>
                  <p><strong>Level:</strong> {selectedLog.level}</p>
                  <p><strong>Message:</strong> {selectedLog.message}</p>
                  {selectedLog.level === 'Error' && (
                    <div>
                      <p><strong>Codingan Error:</strong></p>
                      <pre style={{
                        backgroundColor: '#2d2d2d',
                        color: '#f8f8f2',
                        padding: '15px',
                        borderRadius: '5px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        overflowX: 'auto',
                      }}>
                        <code>
{`// Example error code
function faultyFunction() {
  throw new Error('This is a simulated error.');
}`}
                        </code>
                      </pre>
                    </div>
                  )}
                </div>
              ) : (
                <p>No log selected.</p>
              )}
            </Offcanvas.Body>
          </Offcanvas>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Logs;
